import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Bike, Ban, Coins, MapPin } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { money } from '../lib/constants';
import { Screen } from '../customer/PhoneFrame';

const dayStr = (offset = 0) => { const d = new Date(); d.setDate(d.getDate() - offset); return d.toISOString().slice(0, 10); };
const label = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('es-HN', { weekday: 'short', day: '2-digit', month: 'short' }) : '-');

const RANGES = [
  { key: 'today', label: 'Hoy', from: () => dayStr(0), to: () => dayStr(0) },
  { key: 'yesterday', label: 'Ayer', from: () => dayStr(1), to: () => dayStr(1) },
  { key: 'week', label: 'Últimos 7 días', from: () => dayStr(6), to: () => dayStr(0) },
];

export const DriverHistory = () => {
  const { driver, driverHistory, orders } = useApp();
  const [range, setRange] = useState({ from: dayStr(0), to: dayStr(0) });
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!driver?.id) return;
    setLoading(true);
    driverHistory({ driverId: driver.id, from: range.from, to: range.to })
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [driver?.id, driverHistory, range.from, range.to]);

  // Live: refetch whenever orders change (WebSocket keeps the context list fresh).
  useEffect(() => { load(); }, [load, orders]);

  const isToday = range.from === dayStr(0) && range.to === dayStr(0);
  const rows = useMemo(() => {
    const text = q.trim().toLowerCase();
    return (data?.orders || []).filter((o) => !text || [o.id, o.restaurant, o.customer, o.address].some((v) => String(v || '').toLowerCase().includes(text)));
  }, [data, q]);

  return (
    <Screen nav>
      <div className="px-4 pt-3 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-[#EE3B36]">Historial</h1>
        <button data-testid="history-refresh" onClick={load} aria-label="Actualizar" className="w-9 h-9 rounded-full bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center press"><RefreshCw size={18} className={loading ? 'animate-spin' : ''} /></button>
      </div>

      <div className="px-4 mt-3 flex gap-2 overflow-x-auto no-scrollbar">
        {RANGES.map((r) => {
          const on = range.from === r.from() && range.to === r.to();
          return <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setRange({ from: r.from(), to: r.to() })} className={`px-4 h-9 rounded-full text-sm font-semibold border whitespace-nowrap press ${on ? 'bg-[#EE3B36] text-white border-[#EE3B36]' : 'bg-white text-gray-600 border-gray-200'}`}>{r.label}</button>;
        })}
      </div>

      <div className="mx-4 mt-3 rounded-2xl border border-gray-200 p-3">
        <p className="text-[11px] font-semibold text-gray-500">Desde - Hasta</p>
        <div className="flex items-center gap-2 mt-1.5">
          <input data-testid="driver-history-from" type="date" value={range.from} max={range.to} onChange={(e) => setRange({ ...range, from: e.target.value })} className="flex-1 min-w-0 h-10 px-2.5 rounded-xl border border-gray-200 text-sm" />
          <span className="text-gray-400">-</span>
          <input data-testid="driver-history-to" type="date" value={range.to} min={range.from} onChange={(e) => setRange({ ...range, to: e.target.value })} className="flex-1 min-w-0 h-10 px-2.5 rounded-xl border border-gray-200 text-sm" />
        </div>
      </div>

      <div className="mx-4 mt-3 relative">
        <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input data-testid="driver-history-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar ..." className="w-full h-11 pl-10 pr-3 rounded-2xl border border-gray-200 text-sm outline-none focus:border-[#EE3B36]" />
      </div>

      <div className="mx-4 mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-[#E9F7EF] border border-[#22A45D]/25 p-3 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-[#22A45D] text-white flex items-center justify-center shrink-0"><Bike size={17} /></span>
          <div className="min-w-0"><p data-testid="history-done-count" className="text-xl font-extrabold leading-none">{data?.completed ?? '…'}</p><p className="text-[11px] text-[#15603A] font-semibold">Realizados</p></div>
        </div>
        <div className="rounded-xl bg-[#FDECEC] border border-[#EE3B36]/25 p-3 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-[#EE3B36] text-white flex items-center justify-center shrink-0"><Ban size={17} /></span>
          <div className="min-w-0"><p data-testid="history-rejected-count" className="text-xl font-extrabold leading-none">{data?.rejected ?? '…'}</p><p className="text-[11px] text-[#9B1C18] font-semibold">Rechazados</p></div>
        </div>
      </div>

      <div className="mx-4 mt-3 rounded-xl bg-[#1E2130] text-white p-3 flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Coins size={17} /></span>
        <div className="min-w-0 flex-1"><p className="text-[11px] text-white/70">{isToday ? 'Ganancias de hoy' : `Ganancias del ${label(range.from)} al ${label(range.to)}`}</p><p data-testid="history-pay-total" className="text-xl font-extrabold leading-tight">{data ? money(data.pay) : '…'}</p></div>
      </div>

      <div data-testid="driver-history-list" className="px-4 mt-3 space-y-2 pb-4">
        {!loading && rows.length === 0 && <p className="text-center text-gray-400 text-sm py-10">No hay pedidos en estas fechas</p>}
        {rows.map((o) => {
          const done = o.status === 'completed';
          return (
            <div key={o.id} data-testid={`history-item-${o.id}`} className={`rounded-xl border p-3 ${done ? 'bg-[#F3FAF6] border-[#22A45D]/20' : 'bg-[#FEF5F5] border-[#EE3B36]/20'}`}>
              <div className="flex items-start justify-between gap-2">
                <p className={`text-sm font-extrabold ${done ? 'text-[#15603A]' : 'text-[#9B1C18]'}`}>{o.restaurant} <span className="text-gray-500 font-semibold">#{o.id}</span></p>
                {done && <span className="text-sm font-extrabold text-[#15603A] whitespace-nowrap">+{money(o.pay)}</span>}
              </div>
              <p className="text-xs text-gray-600 mt-1 flex items-start gap-1.5 [overflow-wrap:anywhere]"><MapPin size={13} className="shrink-0 mt-0.5" />{o.address || o.customer}</p>
              <p className="text-[11px] text-gray-500 mt-1">{label(o.day)} • {o.time} • {done ? 'Realizado' : o.rejectedReason === 'timeout' ? 'Rechazado (no aceptado a tiempo)' : 'Rechazado'}</p>
            </div>
          );
        })}
      </div>
    </Screen>
  );
};

export default DriverHistory;
